// src/pages/GestionUsuarios.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";
import { collection, getDocs, query, where, doc, setDoc, deleteDoc } from "firebase/firestore";

export default function GestionUsuarios() {
  const navigate = useNavigate();
  const { user, role, companyId } = useAuth();
  
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [nuevoUsuario, setNuevoUsuario] = useState({
    email: "",
    password: "",
    displayName: "",
    role: "employee"
  });

  // 🔐 Solo company_admin puede acceder
  useEffect(() => {
    if (role !== "company_admin") {
      navigate("/");
    }
  }, [role, navigate]);

  // 📋 Cargar usuarios de la empresa
  useEffect(() => {
    if (companyId) {
      cargarUsuarios();
    }
  }, [companyId]);

  const cargarUsuarios = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, "users"),
        where("companyId", "==", companyId)
      );
      
      const snapshot = await getDocs(q);
      const usuariosList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setUsuarios(usuariosList);
    } catch (error) {
      console.error("Error cargando usuarios:", error);
      alert("Error al cargar usuarios: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCrearUsuario = async (e) => {
    e.preventDefault();
    
    if (!nuevoUsuario.email || !nuevoUsuario.password || !nuevoUsuario.displayName) {
      alert("Por favor completa todos los campos");
      return;
    }

    try {
      setLoading(true);

      // Llamar a la función de Firebase para crear usuario
      const CREATE_USER_URL = import.meta.env.VITE_CREATE_USER_URL;
      
      if (!CREATE_USER_URL) {
        throw new Error("URL de creación de usuario no configurada");
      }

      const idToken = await user.getIdToken();
      
      const response = await fetch(CREATE_USER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`
        },
        body: JSON.stringify({
          email: nuevoUsuario.email,
          password: nuevoUsuario.password,
          displayName: nuevoUsuario.displayName,
          role: nuevoUsuario.role,
          companyId: companyId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Error al crear usuario");
      }

      const result = await response.json();
      
      alert(`Usuario creado exitosamente: ${result.user.email}`);
      
      // Limpiar formulario
      setNuevoUsuario({
        email: "",
        password: "",
        displayName: "",
        role: "employee"
      });
      
      setShowModal(false);
      cargarUsuarios(); // Recargar lista
      
    } catch (error) {
      console.error("Error creando usuario:", error);
      alert("Error al crear usuario: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarUsuario = async (userId, userEmail) => {
    if (!confirm(`¿Estás seguro de eliminar al usuario ${userEmail}?`)) {
      return;
    }

    try {
      setLoading(true);
      
      // TODO: Llamar a función Firebase para eliminar usuario de Authentication
      // Por ahora solo eliminamos del documento de Firestore
      await deleteDoc(doc(db, "users", userId));
      
      alert("Usuario eliminado exitosamente");
      cargarUsuarios();
      
    } catch (error) {
      console.error("Error eliminando usuario:", error);
      alert("Error al eliminar usuario: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const roles = {
      super_admin: { label: "Super Admin", color: "#dc2626" },
      company_admin: { label: "Administrador", color: "#2563eb" },
      employee: { label: "Empleado", color: "#059669" },
      viewer: { label: "Visualizador", color: "#6b7280" }
    };
    
    const roleInfo = roles[role] || { label: role, color: "#6b7280" };
    
    return (
      <span style={{
        padding: "4px 12px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: "600",
        backgroundColor: roleInfo.color + "20",
        color: roleInfo.color
      }}>
        {roleInfo.label}
      </span>
    );
  };

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        marginBottom: "30px"
      }}>
        <div>
          <h1 style={{ margin: "0 0 8px 0", fontSize: "28px", color: "#1f2937" }}>
            Gestión de Usuarios
          </h1>
          <p style={{ margin: 0, color: "#6b7280" }}>
            Administra los usuarios de tu empresa
          </p>
        </div>
        
        <button
          onClick={() => setShowModal(true)}
          disabled={loading}
          style={{
            padding: "12px 24px",
            backgroundColor: "#2563eb",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.5 : 1
          }}
        >
          + Nuevo Usuario
        </button>
      </div>

      {/* Lista de Usuarios */}
      {loading && !showModal ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
          Cargando usuarios...
        </div>
      ) : usuarios.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
          No hay usuarios registrados
        </div>
      ) : (
        <div style={{
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          overflow: "hidden"
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ backgroundColor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
              <tr>
                <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: "#6b7280", textTransform: "uppercase" }}>
                  Usuario
                </th>
                <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: "#6b7280", textTransform: "uppercase" }}>
                  Email
                </th>
                <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: "#6b7280", textTransform: "uppercase" }}>
                  Rol
                </th>
                <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "12px", fontWeight: "600", color: "#6b7280", textTransform: "uppercase" }}>
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((usuario) => (
                <tr key={usuario.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "16px", color: "#1f2937", fontWeight: "500" }}>
                    {usuario.displayName || usuario.email.split("@")[0]}
                  </td>
                  <td style={{ padding: "16px", color: "#6b7280" }}>
                    {usuario.email}
                  </td>
                  <td style={{ padding: "16px" }}>
                    {getRoleBadge(usuario.role)}
                  </td>
                  <td style={{ padding: "16px", textAlign: "center" }}>
                    {usuario.id !== user?.uid && (
                      <button
                        onClick={() => handleEliminarUsuario(usuario.id, usuario.email)}
                        disabled={loading}
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#fee2e2",
                          color: "#dc2626",
                          border: "none",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor: loading ? "not-allowed" : "pointer",
                          opacity: loading ? 0.5 : 1
                        }}
                      >
                        Eliminar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Crear Usuario */}
      {showModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "32px",
            maxWidth: "500px",
            width: "90%",
            maxHeight: "90vh",
            overflow: "auto"
          }}>
            <h2 style={{ margin: "0 0 24px 0", fontSize: "24px", color: "#1f2937" }}>
              Crear Nuevo Usuario
            </h2>

            <form onSubmit={handleCrearUsuario}>
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#374151" }}>
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  value={nuevoUsuario.displayName}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, displayName: e.target.value })}
                  required
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box"
                  }}
                  placeholder="Ej: Juan Pérez"
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#374151" }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={nuevoUsuario.email}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, email: e.target.value })}
                  required
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box"
                  }}
                  placeholder="usuario@empresa.com"
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#374151" }}>
                  Contraseña *
                </label>
                <input
                  type="password"
                  value={nuevoUsuario.password}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, password: e.target.value })}
                  required
                  minLength={6}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box"
                  }}
                  placeholder="Mínimo 6 caracteres"
                />
              </div>

              <div style={{ marginBottom: "24px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#374151" }}>
                  Rol *
                </label>
                <select
                  value={nuevoUsuario.role}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, role: e.target.value })}
                  required
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box"
                  }}
                >
                  <option value="employee">Empleado</option>
                  <option value="company_admin">Administrador</option>
                  <option value="viewer">Visualizador</option>
                </select>
              </div>

              <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#f3f4f6",
                    color: "#374151",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: loading ? "not-allowed" : "pointer"
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#2563eb",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.5 : 1
                  }}
                >
                  {loading ? "Creando..." : "Crear Usuario"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}